import { ProductCardContainer } from "/product-card";
import { ProductCardFlatWide } from "/product-card-flat-wide";
import { ProductBlocksComplementary } from "_types";
import clsx from "clsx";
import { _Product, useGlobalProducts } from "global-stores";
import { ReactProductState } from "product";
import { ProductCardFlat } from "product-card-flat";
import { ProductCardSmall } from "product-card-small";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { ScrollBar } from "scrollbar";
import { Typography } from "typography";
import { StoreApi, UseBoundStore } from "zustand";

export const ProductComplementaryItems: FC<{
  settings: ProductBlocksComplementary["settings"];
  useProduct: UseBoundStore<StoreApi<ReactProductState>>;
}> = ({ settings, useProduct }) => {
  const { product } = useProduct(({ product }) => ({ product }));
  const { hydrated } = useGlobalProducts(({ hydrated }) => ({ hydrated }));
  const productId = useRef(product?.id);

  const [products, setProducts] = useState([]);

  const loadComplementaryProducts = useCallback(async () => {
    let complementaryProducts = (
      settings.source === "complementary"
        ? [
            ...(product?._complementary ?? []),
            ...((product?.metafields?.["shopify--discovery--product_recommendation"]
              ?.complementary_products as _Product[]) ?? []),
          ]
        : settings.source === "complementary_first"
        ? [
            ...(product?._complementary ?? []),
            ...((product?.metafields?.["shopify--discovery--product_recommendation"]
              ?.complementary_products as _Product[]) ?? []),
            ...(product?._related ?? []),
            ...((product?.metafields?.["shopify--discovery--product_recommendation"]
              ?.related_products as _Product[]) ?? []),
          ]
        : [
            ...(product?._related ?? []),
            ...((product?.metafields?.["shopify--discovery--product_recommendation"]
              ?.related_products as _Product[]) ?? []),
          ]
    ) as _Product[];

    if (!settings.hide_upsell_products || complementaryProducts.length === 0) {
      complementaryProducts = [...complementaryProducts, ...(settings.products as _Product[])];
    }

    const finalProducts = await Promise.all(
      complementaryProducts
        ?.filter((p, i, arr) => p.id !== product?.id && arr.findIndex((p2) => p2.id === p.id) === i)
        ?.map((product) => window.transformProducts(product))
        ?.filter((product) => !product?.hidden)
        ?.slice(0, settings.limit)
        ?.map((p) => window.useGlobalProducts.getState().getUntransformedAsyncProduct(p.handle))
    );

    setProducts(
      finalProducts
        ?.map((product) => window.transformProducts(product))
        ?.filter((product) => !product?.hidden)
    );
    useProduct.getState().triggerSideEffect();
  }, [
    product?._complementary,
    product?._related,
    product?.id,
    product?.metafields,
    settings.hide_upsell_products,
    settings.limit,
    settings.products,
    settings.source,
    useProduct,
  ]);

  useEffect(() => {
    if (hydrated || productId.current !== product?.id) {
      productId.current = product.id;
      loadComplementaryProducts();
    }
  }, [loadComplementaryProducts, hydrated, product.id]);

  return (
    <div className="pb-4">
      {products.length > 0
        ? <>
            <Typography fontType={settings.title_font as 1 | 2 | 3}>{settings.title}</Typography>
            <main className="relative py-2">
              {products.length === 1 && settings.style === "small"
                ? <div className="grid gap-4">
                    {products?.map((upsellProduct) =>
                      settings.style === "small"
                        ? <ProductCardFlat key={upsellProduct.id} product={upsellProduct} />
                        : <ProductCardContainer key={upsellProduct.id} product={upsellProduct} />
                    )}
                  </div>
                : settings.style === "flat"
                ? <div className="grid gap-4 rounded-theme border border-gray-200 p-5">
                    {products?.map((upsellProduct, index) => (
                      <>
                        {index !== 0 ? <hr className="border-t border-gray-200" /> : null}
                        <ProductCardFlatWide key={upsellProduct.id} product={upsellProduct} />
                      </>
                    ))}
                  </div>
                : <div
                    className={clsx(
                      "scrollbar-none -my-8 grid snap-x snap-mandatory scroll-pl-8 grid-flow-col-dense gap-4 overflow-x-auto py-8 "
                    )}
                    style={{
                      gridAutoColumns: settings.style === "small" ? "170px" : "calc(50% - 8px)",
                    }}
                  >
                    {products?.map((upsellProduct) =>
                      settings.style === "small"
                        ? <ProductCardSmall key={upsellProduct.id} product={upsellProduct} />
                        : <ProductCardContainer key={upsellProduct.id} product={upsellProduct} />
                    )}
                    <ScrollBar itemCount={products.length} />
                  </div>}
            </main>
          </>
        : null}
    </div>
  );
};
